import React, { ReactNode } from "react";
import Grid from "@able/react/dist/Grid";
import Column from "@able/react/dist/Column";
import TextStyle from "@able/react/dist/TextStyle";
import Spacing from "@able/react/dist/Spacing";
import ActionButton, { ActionButtonProps } from "@able/react/dist/ActionButton";
import "./style.scss";

export type titleHeadingOrder = 1 | 2 | 3 | 4 | 5 | 6;
export type SubheaderHeadingOrder = 2 | 3 | 4 | 5 | 6;


export type MessageTemplateProps = {
    title: string | JSX.Element[] | JSX.Element;
    titleHeadingOrder?: titleHeadingOrder;
    subheading?: string | JSX.Element[] | JSX.Element;
    subheaderHeadingOrder?: SubheaderHeadingOrder;
    content: ReactNode;
    primaryButton: ActionButtonProps;
    secondaryButton?: ActionButtonProps;
    imageSrc: string;
}

/**
 * Message Template is a simple way for users to show any important message with headings, sub headings, image and buttons.
 *
 *
 * @version 1.0.0
 */

const MessageTemplate: React.FC<MessageTemplateProps> = ({
    title,
    titleHeadingOrder = 1,
    subheading,
    subheaderHeadingOrder,
    content,
    primaryButton,
    secondaryButton,
    imageSrc,
}) => {
    const autoSubheaderHeadingOrder =
        subheaderHeadingOrder ||
        titleHeadingOrder + 1 as SubheaderHeadingOrder;
    if (
        autoSubheaderHeadingOrder !== titleHeadingOrder + 1
    ) {
        console.error(
            "Accessibility Alert: The subheading should be one level lower than primary heading to ensure proper heading hierarchy and accessibility."
        );
    }
    if (
        subheading && titleHeadingOrder >= 6
    ) {
        console.error(
            "Accessibility Alert: Since tile has 'h6' heading, subheadings are not shown. Please avoid using subheading or consider using higher heading for title"
        );
    }

    return (
        <Grid>
            <Column cols={12} bsm={6} bmd={7} className="tui-message-page-content">
                <TextStyle element={`h${titleHeadingOrder}`} alias="HeadingA">
                    <>{title}</>
                </TextStyle>
                <Spacing bottom="spacing3x" bottomVXS="spacing2x" bottomVSM="spacing2x" bottomVMD="spacing2x" />
                {subheading && titleHeadingOrder < 6 && (
                    <>
                        <TextStyle
                            element={`h${autoSubheaderHeadingOrder}`}
                            alias="HeadingC">
                            <>{subheading}</>
                        </TextStyle>
                        <Spacing bottom="spacing3x" bottomVXS="spacing2x" bottomVSM="spacing2x" bottomVMD="spacing2x" />
                    </>
                )}
                {content}
                <Spacing bottom="spacing5x" />
                <ActionButton {...primaryButton} />
                <Spacing bottom="spacing3x" bottomVXS="spacing2x" bottomVSM="spacing2x" bottomVMD="spacing2x" />
                {secondaryButton && (
                    <>
                        <ActionButton {...secondaryButton} />
                    </>
                )}
            </Column>
            <Column cols={12} bsm={6} bmd={5} className="tui-message-page-img-container">
                <img alt="" src={imageSrc} className="tui-message-page-img" />
            </Column>
        </Grid >
    );
};

export default MessageTemplate;
